import {
  IonApp,
  IonIcon,
  IonLabel,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
  setupIonicReact
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { briefcase, people, person, chatbubbles, mic } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import AuthStatus from './components/AuthStatus.tsx';
import InsufficientBalanceModal from './components/InsufficientBalanceModal.tsx';
import ProtectedRoute from './components/ProtectedRoute.tsx';
import { AuthProvider } from './context/AuthContext.tsx';
import AuthCallback from './pages/AuthCallback.tsx';
import Billing from './pages/Billing.tsx';
import ContactDetails from './pages/ContactDetails.tsx';
import DraftEmail from './pages/DraftEmail.tsx';
import JobDetailPage from './pages/JobDetailPage.tsx';
import Jobs from './pages/Jobs.tsx';
import Login from './pages/Login.tsx';
import Network from './pages/Network.tsx';
import OAuthSuccess from './pages/OAuthSuccess.tsx';
import Profile from './pages/Profile.tsx';
import PublicProfile from './pages/PublicProfile.tsx';
import ResumeAnalysis from './pages/ResumeAnalysis.tsx';
import SavedJobs from './pages/SavedJobs.tsx';
import ScrapedJobDetail from './pages/ScrapedJobDetail.tsx';
import Settings from './pages/Settings.tsx';
import Usage from './pages/Usage.tsx';
import PreferencesPage from './pages/PreferencesPage.tsx';
import Agent from './pages/Agent.tsx';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';
/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';
/* Optional CSS utils that can be commented out */
import '@ionic/react/css/display.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/padding.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
/* Theme variables */
import './theme/variables.css';
import './theme/global.css';

setupIonicReact();

// Workaround for TS type mismatch in IonReactRouter props in this setup
const Router: any = IonReactRouter;

const App: React.FC = () => {
  const [insufficientBalanceModal, setInsufficientBalanceModal] = useState<{
    isOpen: boolean;
    error?: any;
  }>({
    isOpen: false
  });

  useEffect(() => {
    const handleInsufficientBalance = (event: CustomEvent) => {
      console.log('💰 App.tsx: Received insufficient-balance event:', event.detail);
      setInsufficientBalanceModal({
        isOpen: true,
        error: event.detail
      });
    };

    console.log('💰 App.tsx: Setting up insufficient-balance event listener');
    window.addEventListener('insufficient-balance', handleInsufficientBalance as EventListener);

    return () => {
      window.removeEventListener('insufficient-balance', handleInsufficientBalance as EventListener);
    };
  }, []);

  const closeInsufficientBalanceModal = () => {
    setInsufficientBalanceModal({ isOpen: false });
  };

  return (
    <IonApp>
      <AuthProvider>
        <Router>
          <Switch>
            {/* Public routes */}
            <Route exact path="/login" component={Login} />
            <Route exact path="/profile/:shareToken" component={PublicProfile} />
            <Route exact path="/auth/callback" component={AuthCallback} />
            <Route exact path="/oauth-success" component={OAuthSuccess} />

            {/* Stripe redirect routes */}
            <Route exact path="/credits/success" render={({ location }: any) => {
              const params = new URLSearchParams(location.search);
              const sessionId = params.get('session_id');
              return <Redirect to={`/app/settings/billing?status=success&session_id=${sessionId}`} />;
            }} />
            <Route exact path="/credits" render={({ location }: any) => {
              const params = new URLSearchParams(location.search);
              const canceled = params.get('canceled');
              return <Redirect to={`/app/settings/billing${canceled ? '?status=cancelled' : ''}`} />;
            }} />

            {/* Redirect common incorrect routes to correct app routes */}
            <Route exact path="/network">
              <Redirect to="/app/network" />
            </Route>
            <Route exact path="/jobs">
              <Redirect to="/app/jobs" />
            </Route>
            <Route exact path="/profile">
              <Redirect to="/app/profile" />
            </Route>

            <Route exact path="/">
              <Redirect to="/app/profile" />
            </Route>

            {/* Protected app routes */}
            <Route path="/app">
              <IonTabs>
                <IonRouterOutlet>
                  <Switch>
                    {/* More specific job routes first */}
                    <ProtectedRoute exact path="/app/jobs/saved" component={SavedJobs} />
                    <ProtectedRoute exact path="/app/jobs/scraped/:id" component={ScrapedJobDetail} />
                    <ProtectedRoute exact path="/app/jobs/:id" component={JobDetailPage} />
                    <ProtectedRoute exact path="/app/jobs" component={Jobs} />
                    <ProtectedRoute exact path="/app/network" component={Network} />
                    <ProtectedRoute exact path="/app/agent" component={Agent} />
                    <ProtectedRoute path="/app/profile/analysis/:resumeId" component={ResumeAnalysis} />
                    <ProtectedRoute exact path="/app/profile/preferences" component={PreferencesPage} />
                    <ProtectedRoute exact path="/app/profile" component={Profile} />
                    <ProtectedRoute exact path="/app/settings/billing" component={Billing} />
                    <ProtectedRoute exact path="/app/settings/usage" component={Usage} />
                    <ProtectedRoute exact path="/app/settings" component={Settings} />
                    {/* Redirect /app/billing to /app/settings/billing for convenience */}
                    <Route exact path="/app/billing">
                      <Redirect to="/app/settings/billing" />
                    </Route>
                    <ProtectedRoute path="/app/network/contact/:id" component={ContactDetails} />
                    <ProtectedRoute path="/app/network/contact/:id/email" component={() => <DraftEmail isReferral={false} />} />
                    <ProtectedRoute path="/app/network/contact/:id/job/:jobId/email" component={() => <DraftEmail isReferral={true} />} />
                    <Route exact path="/app">
                      <Redirect to="/app/profile" />
                    </Route>
                    {/* Catch any other /app/* routes */}
                    <Route path="/app/*">
                      <Redirect to="/app/profile" />
                    </Route>
                  </Switch>
                </IonRouterOutlet>

                <IonTabBar slot="bottom">
                  <IonTabButton tab="network" href="/app/network">
                    <IonIcon icon={people} />
                    <IonLabel>Network</IonLabel>
                  </IonTabButton>
                  <IonTabButton tab="agent" href="/app/agent">
                    <IonIcon icon={mic} />
                    <IonLabel>Agent</IonLabel>
                  </IonTabButton>
                  <IonTabButton tab="jobs" href="/app/jobs">
                    <IonIcon icon={briefcase} />
                    <IonLabel>Jobs</IonLabel>
                  </IonTabButton>
                  <IonTabButton tab="profile" href="/app/profile">
                    <IonIcon icon={person} />
                    <IonLabel>Profile</IonLabel>
                  </IonTabButton>
                </IonTabBar>
              </IonTabs>
            </Route>

            {/* Catch-all route for any other unmatched paths */}
            <Route path="*">
              <Redirect to="/app/profile" />
            </Route>
          </Switch>

          <AuthStatus />
        </Router>

        <InsufficientBalanceModal
          isOpen={insufficientBalanceModal.isOpen}
          onClose={closeInsufficientBalanceModal}
          balanceError={insufficientBalanceModal.error}
        />
      </AuthProvider>
    </IonApp>
  );
};

export default App;